import { LitElement } from "@lit";
import { t } from "@nova/utils";

class SeamGuidelinesBanner extends LitElement {
  static properties = {
    seamThresholdInch: { type: Number, attribute: "seam-threshold-inch" },
    _inputWidth: { type: Number, state: true },
    _inputLength: { type: Number, state: true },
    isCustom: { type: Boolean, attribute: 'is-custom' }
  };

  constructor() {
    super();
    this._inputWidth = 0;
    this._inputLength = 0;
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.isCustom) {
      this.customCalculatorValueEl = document.querySelector(
        "nova-custom-calculator-values",
      );
  
      this.customCalculatorValueEl?.addEventListener(
        "custom-calculator:values-updated",
        this.onCalculatorValuesUpdated,
      );
  
      this.unitSelector = document.querySelector(".js-custom-size-unit-type");
      this.unitSelector?.addEventListener("sl-change", this.resetDisplay);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.isCustom) {
      this.customCalculatorValueEl?.removeEventListener(
        "custom-calculator:values-updated",
        this.onCalculatorValuesUpdated,
      );
      
      this.unitSelector?.removeEventListener("sl-change", this.resetDisplay);
    }
  }

  willUpdate(changedProperties) {
    if (
      changedProperties.has("_inputWidth") ||
      changedProperties.has("_inputLength")
    ) {
      if (this.isCustom) { 
        const shouldShow =
        this._inputLength > this.seamThresholdInch &&
        this._inputWidth > this.seamThresholdInch;
        this.hidden = !shouldShow;
      }
    }
  }

  onCalculatorValuesUpdated = (event) => {
    if (!event.detail) return;
    const { value, type } = event?.detail;

    if (type == "w") {
      this._inputWidth = value;
    } else if (type == "h") {
      this._inputLength = value;
    }
  };

  resetDisplay = () => {
    this._inputWidth = 0;
    this._inputLength = 0;
  };

  createRenderRoot() {
    return this;
  }
}

if (typeof customElements.get("seam-guidelines-banner") === "undefined") {
  customElements.define("seam-guidelines-banner", SeamGuidelinesBanner);
}

class SeamGuidelinesDialogButton extends LitElement {
  constructor() {
    super();
  }

  dialogButton = null;

  dialogEl = null;

  connectedCallback() {
    super.connectedCallback();

    this.dialogButton = this.querySelector("button");
    if (!this.dialogButton) return;
    const dialogId = this.dialogButton.getAttribute("aria-controls");
    this.dialogEl = document.querySelector(`#${dialogId}`);

    this.dialogButton?.addEventListener(
      "click",
      this.handleDialogButton.bind(this),
    );
    this.dialogEl?.addEventListener(
      "sl-after-show",
      this.handleDialogButtonStatus.bind(this),
    );
    this.dialogEl?.addEventListener(
      "sl-after-hide",
      this.handleDialogButtonStatus.bind(this),
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.dialogButton?.removeEventListener("click", this.handleDialogButton);
    this.dialogEl?.removeEventListener(
      "sl-after-show",
      this.handleDialogButtonStatus,
    );
    this.dialogEl?.removeEventListener(
      "sl-after-hide",
      this.handleDialogButtonStatus,
    );
  }

  handleDialogButton(event) {
    this.dialogEl.show();
  }

  handleDialogButtonStatus() {
    const isOpen = this.dialogEl.hasAttribute("open");
    this.dialogButton.setAttribute("aria-expanded", String(isOpen));
  }

  createRenderRoot() {
    return this;
  }
}

if (
  typeof customElements.get("seam-guidelines-dialog-button") === "undefined"
) {
  customElements.define(
    "seam-guidelines-dialog-button",
    SeamGuidelinesDialogButton,
  );
}

class SeamGuidelinesDialogInputGroup extends LitElement {
  /**
   * @type {HTMLInputElement[] | null}
   */
  dialogInputFields = null;

  /**
   * @type {HTMLInputElement[] | null}
   */
  customValueInputFields = null;

  /**
   * @type {HTMLInputElement[] | null}
   */
  seamGuidelinesDialog = null;

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();

    this.customCalculatorValueEl = document.querySelector(
      "nova-custom-calculator-values",
    );

    this.seamGuidelinesDialog = this.closest("sl-drawer");
    this.seamGuidelinesDialog.addEventListener(
      "sl-show",
      this.handleDrawerOpen,
    );

    this.customValueInputFields = this.customCalculatorValueEl?.inputFields;
    this.customValueInputFields?.forEach((input) => {
      input?.addEventListener("sl-change", this.handleCustomValueChange);
    });

    this.dialogInputFields = this.querySelectorAll("sl-input");
    this.dialogInputFields?.forEach((input) => {
      input.addEventListener("sl-change", this.handleInputChange.bind(this));
    });

    this.customCalculatorValueEl.addEventListener(
      "custom-calculator:values-swapped",
      this.syncValues.bind(this),
    );

    this.unitSelector = document.querySelector(".js-custom-size-unit-type");
    this.unitSelector?.addEventListener(
      "sl-change",
      this.updateUnitType.bind(this),
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.seamGuidelinesDialog.removeEventListener(
      "sl-show",
      this.handleDrawerOpen,
    );

    this.customValueInputFields?.forEach((input) => {
      input.removeEventListener("sl-change", this.handleCustomValueChange);
    });

    this.dialogInputFields?.forEach((input) => {
      input.removeEventListener("sl-change", this.handleInputChange);
    });

    this.customCalculatorValueEl.removeEventListener(
      "custom-calculator:values-swapped",
      this.syncValues,
    );

    this.unitSelector?.removeEventListener("sl-change", this.updateUnitType);
  }

  findInputByName(inputs, name) {
    return [...inputs]?.find((input) => {
      return input.name == name;
    });
  }

  handleDrawerOpen = () => {
    this.customValueInputFields = this.customCalculatorValueEl?.inputFields;

    this.syncValues();
  };

  handleCustomValueChange = (event) => {
    const inputName = event.target?.name;

    const dialogInput = this.findInputByName(this.dialogInputFields, inputName);
    if (!dialogInput) return;

    dialogInput.value = event.target?.value;
  };

  handleInputChange(event) {
    const inputName = event.target?.name;

    const customInput = this.findInputByName(
      this.customValueInputFields,
      inputName,
    );

    customInput.value = event.target?.value;

    customInput.dispatchEvent(new CustomEvent("sl-blur"));
  }

  syncValues() {
    this.customValueInputFields.forEach((input) => {
      const inputName = input.name;
      const matchedDialogInput = this.findInputByName(
        this.dialogInputFields,
        inputName,
      );

      matchedDialogInput.value = input.value;
    });
  }

  updateUnitType(event) {
    const value = event?.target.value;

    if (!value) return;

    this.setAttribute("data-unit", value);

    this.dialogInputFields?.forEach((input) => {
      input.value = "";
    });
  }

  createRenderRoot() {
    return this;
  }
}

if (typeof customElements.get("seam-guidelines-input-group") === "undefined") {
  customElements.define(
    "seam-guidelines-input-group",
    SeamGuidelinesDialogInputGroup,
  );
}

class SeamGuidelinesDialogInputGroupStandard extends LitElement {
  seamGuidelinePreviewWrapperEl = null;

  static properties = {
    _inputValues: {
      type: Object,
      state: true,
    },
    _totalValues: {
      type: Object,
      state: true,
    },
    _minMaxValues: {
      type: Object,
      attribute: 'min-max-values'
    }
  };

  constructor() {
    super();
    this._inputValues = {
      wFeet: 0,
      wInch: 0,
      lFeet: 0,
      lInch: 0,
    };

    this._totalValues = {
      width: 0,
      length: 0,
    };
  }

  _mappedInputNames = {
    "w-1": "wFeet",
    "w-2": "wInch",
    "h-1": "lFeet",
    "h-2": "lInch",
  };

  connectedCallback() {
    super.connectedCallback();

    this.dialogInputFields = this.querySelectorAll("sl-input");
    this.dialogInputFields?.forEach((input) => {
      input.addEventListener("sl-change", this.handleInputChange);
    });

    this.seamGuidelinePreviewWrapperEl = document.querySelector(
      "#custom-seam-guidelines-preview--standard",
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.dialogInputFields?.forEach((input) => {
      input.removeEventListener("sl-change", this.handleInputChange);
    });
  }

  handleInputChange = (event) => {
    const inputName = event.target?.name;
    const key = this._mappedInputNames[inputName];
    const value = event.target?.value;

    this._inputValues = { ...this._inputValues, [key]: Number(value) };
  }

  willUpdate(changedProperties) {
    if (changedProperties.has("_inputValues")) {
      this.calculateTotalValues();
    } else if (changedProperties.has("_totalValues")) {
      this.setPreviewValues();
    }
  }

  calculateTotalValues() {
    let totalWidth = this._inputValues.wFeet * 12 + this._inputValues.wInch;
    let totalLength = this._inputValues.lFeet * 12 + this._inputValues.lInch;

    if (totalWidth > totalLength && totalWidth > 0 && totalLength > 0) {
      this.swapInputs();
      [totalWidth, totalLength] = [totalLength, totalWidth];
    }

    this._totalValues = {
      ...this._totalValues,
      width: totalWidth,
      length: totalLength,
    };
    
    this.checkMinMaxValues();
    this.setPreviewValues();
    this.requestUpdate();
  }

  swapInputs() {
    const $inputsW = [...this.dialogInputFields].filter(
      (input) => input.dataset.type === "w",
    );
    const oldW = $inputsW.map((item) => item.value);
    const $inputsH = [...this.dialogInputFields].filter(
      (input) => input.dataset.type === "h",
    );

    $inputsH.forEach((item, index) => {
      const wInputKey = this._mappedInputNames[`w-${index + 1}`];
      const hInputKey = this._mappedInputNames[`h-${index + 1}`];
      $inputsW[index].value = item.value;
      this._inputValues[wInputKey] = Number(item.value);

      item.value = oldW[index];
      this._inputValues[hInputKey] = Number(oldW[index]);
    });
  }

  checkMinMaxValues() {
    const dimensions = ['width', 'length']
    const totalValues = this._totalValues
    const minMaxValues = this._minMaxValues

    dimensions.forEach((dimension) => {
      if(totalValues[dimension] > minMaxValues[dimension]['max']) {
        this.setAttribute(`data-error-${dimension}`, `${dimension}-max`)
      } else if(totalValues[dimension] < minMaxValues[dimension]['min'] && totalValues[dimension] > 0) {
        this.setAttribute(`data-error-${dimension}`, `${dimension}-min`)
      } else {
        this.removeAttribute(`data-error-${dimension}`)
      }
    })
  }

  setPreviewValues() {
    if (!this.seamGuidelinePreviewWrapperEl) return;

    this.seamGuidelinePreviewWrapperEl.setAttribute(
      "width",
      this._totalValues.width,
    );
    this.seamGuidelinePreviewWrapperEl.setAttribute(
      "length",
      this._totalValues.length,
    );
  }

  createRenderRoot() {
    return this;
  }
}

if (
  typeof customElements.get("seam-guidelines-input-group-standard") ===
  "undefined"
) {
  customElements.define(
    "seam-guidelines-input-group-standard",
    SeamGuidelinesDialogInputGroupStandard,
  );
}
